<template>
  <transition :name="animation">
    <svg class="ak-loading-spinner" :class="classNames">
      <circle
        class="ak-loading-spinner__path"
        :cx="position"
        :cy="position"
        :r="radius"
        stroke-miterlimit="10"
        fill="none"
      />
    </svg>
  </transition>
</template>

<script>
import { computed, defineComponent } from 'vue'

/**
 * @type {Object}
 * @description These are the available positions for the different sizes.
 */
const positions = {
  default: 50,
  tiny: 12.5,
  small: 25,
}

/**
 * @type {Object}
 * @description These are the available redius for the diferent sizes.
 */
const radii = {
  default: 20,
  tiny: 5,
  small: 10,
}

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-loading-spinner />
   */
  name: 'AkLoadingSpinner',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {},

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Custom animation (transition name)
     */
    animation: {
      type: String,
      default: 'fade',
    },

    /**
     * @type {String}
     * @description The size of the spinner
     */
    size: {
      validator: (v) => ['default', 'small', 'tiny'].indexOf(v) >= 0,
      type: String,
      default: 'default',
    },

    /**
     * @type {String}
     * @description Color of the spinner
     */
    color: {
      type: String,
      default: null,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classNames = {
      [`ak-loading-spinner--${props.size}`]: props.size,
      [`color-${props.color}`]: props.color,
    }

    const position = computed(() => positions[props.size])
    const radius = computed(() => radii[props.size])

    return {
      classNames,
      position,
      radius,
    }
  },

  /**
   * @description Callback for the "created" lifecycle events in vue
   * this will be executed when the component is created
   */
  mounted() {
    /* istanbul ignore else */
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', this.keyPress)
    }
  },

  /**
   * @description Callback for the "beforeDestroy" lifecycle events in vue
   * this will be executed before the component is removed
   */
  beforeUnmount() {
    /* istanbul ignore else */
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', this.keyPress)
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-loading-spinner {
  $size: 100px;
  $self: &;
  animation: rotate 2s linear infinite;
  height: $size;
  width: $size;
  position: relative;
  display: block;
  color: $color-primary;

  &__path {
    stroke-dasharray: 1, 250;
    stroke-dashoffset: 0;
    animation: dash 1.75s ease-in-out infinite;
    stroke-linecap: round;
    stroke: currentColor;
    stroke-width: 0.25em;
  }

  &--small {
    height: calc($size / 2);
    width: calc($size / 2);
    #{$self}__path {
      animation-name: smallDash;
      stroke-width: 0.2em;
    }
  }

  &--tiny {
    height: calc($size / 4);
    width: calc($size / 4);
    #{$self}__path {
      animation-name: tinyDash;
      stroke-width: 0.125em;
    }
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 250;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 180, 250;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 180, 250;
    stroke-dashoffset: -124;
  }
}

@keyframes smallDash {
  0% {
    stroke-dasharray: 1, 250;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 70, 250;
    stroke-dashoffset: -15;
  }
  100% {
    stroke-dasharray: 70, 250;
    stroke-dashoffset: -62;
  }
}

@keyframes tinyDash {
  0% {
    stroke-dasharray: 1, 250;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 60, 250;
    stroke-dashoffset: -10;
  }
  100% {
    stroke-dasharray: 60, 250;
    stroke-dashoffset: -30;
  }
}
</style>
